import { IntlShape } from 'react-intl';
import { RoutePaths, Routes } from '../routes-table';
import { Permission } from '../api/roles.dao';
import { hasPermission } from '../infrastructure/utils/has-permissions';
import { getNavigationLabels } from './navigation-labels.constants';

export interface INavigationLink {
    label: string;
    routeEnum: Routes;
    routeParams?: object;
    routePath?: string;
    isHidden?: boolean;
}

export const getMainNavigationLinks = (accountId: string, intl: IntlShape) => {
    const isAccountSelected = !!accountId;
    const labels = getNavigationLabels(intl);

    return [
        {
            label: labels.ACCOUNTS_LIST,
            routeEnum: Routes.AccountsList,
            routePath: RoutePaths.Accounts,
            isHidden: !hasPermission(Permission.ACCOUNTS_LIST) || isAccountSelected,
        },
        {
            label: labels.ACCOUNT_GROUP_LIST,
            routeEnum: Routes.AccountGroupsList,
            routePath: RoutePaths.AccountGroups,
            isHidden: !hasPermission(Permission.ACCOUNT_GROUPS_LIST) || isAccountSelected,
        },
        {
            label: labels.EVENT_TRACKER_LIST,
            routeEnum: Routes.EventTrackerUpcoming,
            routePath: RoutePaths.EventTracker,
            isHidden: !hasPermission(Permission.EVENT_TRACKER) || isAccountSelected
        },
        {
            label: labels.RESOURCES_LIST,
            routeEnum: Routes.ZoomScheduledMeetings,
            routePath: RoutePaths.Resources,
            isHidden: !hasPermission(Permission.RESOURCES) || isAccountSelected
        },
        {
            label: labels.OPERATORS_LIST,
            routeEnum: Routes.OperatorsList,
            routePath: RoutePaths.Operators,
            isHidden: !hasPermission(Permission.OPERATORS) || isAccountSelected
        },
        {
            label: labels.USERS_LIST,
            routeEnum: Routes.UsersList,
            routePath: RoutePaths.Users,
            isHidden: !hasPermission(Permission.USER_TAB) || isAccountSelected
        },
        {
            label: labels.ROLES_LIST,
            routeEnum: Routes.RolesList,
            routePath: RoutePaths.Roles,
            isHidden: !hasPermission(Permission.ROLES) || isAccountSelected
        },
        {
            label: labels.EVENTS_LIST,
            routeEnum: Routes.EventsList,
            routePath: RoutePaths.Events,
            routeParams: { accountId: accountId },
            isHidden: !isAccountSelected,
        },
        {
            label: labels.EVENT_HUBS,
            routeEnum: Routes.EventHubList,
            routePath: RoutePaths.EventHubs,
            routeParams: { accountId: accountId },
            isHidden: !hasPermission(Permission.EVENT_HUBS_TAB) || !isAccountSelected,
        },
        {
            label: labels.BLUEPRINTS,
            routeEnum: Routes.EventBlueprintList,
            routePath: RoutePaths.Blueprints,
            routeParams: { accountId: accountId },
            isHidden: !hasPermission(Permission.BLUEPRINTS_TAB) || !isAccountSelected,
        },
        {
            label: labels.ACCOUNT_USERS_LIST,
            routeEnum: Routes.AccountUsersList,
            routePath: RoutePaths.AccountUsers,
            routeParams: { accountId: accountId },
            isHidden: !hasPermission(Permission.ACCOUNT_USERS_TAB) || !isAccountSelected,
        },
        {
            label: labels.USER_GROUPS_LIST,
            routeEnum: Routes.UserGroupsList,
            routePath: RoutePaths.UserGroups,
            routeParams: { accountId: accountId },
            isHidden: !hasPermission(Permission.USER_GROUP_ADMIN) || !isAccountSelected,
        },
        {
            label: labels.ACCOUNT_ADMIN,
            routeEnum: Routes.AccountAdmin,
            routePath: RoutePaths.AccountAdmin,
            routeParams: { accountId: accountId },
            isHidden: !hasPermission(Permission.ACCOUNT_ADMIN_TAB) || !isAccountSelected,
        },
        {
            label: labels.EMAIL_TEMPLATES,
            routeEnum: Routes.EmailTemplates,
            routePath: RoutePaths.EmailTemplates,
            routeParams: { accountId: accountId },
            isHidden: !hasPermission(Permission.EMAIL_TEMPLATES_TAB) || !isAccountSelected,
        },
        {
            label: labels.THEMES_LIST,
            routeEnum: Routes.ThemesList,
            routePath: RoutePaths.Themes,
            isHidden: !hasPermission(Permission.THEMES) || isAccountSelected,
        },
        {
            label: labels.OE_DATA,
            routeEnum: Routes.OeDataOrganizations,
            routePath: RoutePaths.OeData,
            isHidden: !hasPermission(Permission.VIEW_OE_DATA_ORGANIZATIONS) || isAccountSelected,
        }
    ];
};

export const getBlueprintsNavigationLinks = (accountId: string, intl: IntlShape): INavigationLink[] => {
    const labels = getNavigationLabels(intl);

    return [
        {
            label: labels.BLUEPRINTS_NAVIGATION.EVENT_BLUEPRINTS,
            routeEnum: Routes.EventBlueprintList,
            routeParams: { accountId: accountId }
        },
        {
            label: labels.BLUEPRINTS_NAVIGATION.MEETING_TEMPLATES,
            routeEnum: Routes.MeetingTemplatesList,
            routeParams: { accountId: accountId }
        },
        {
            label: labels.BLUEPRINTS_NAVIGATION.CONNECTION_TEMPLATES,
            routeEnum: Routes.ConnectionTemplatesList,
            routeParams: { accountId: accountId }
        },
        {
            label: labels.BLUEPRINTS_NAVIGATION.ORDER_TEMPLATES,
            routeEnum: Routes.OrderTemplatesList,
            routeParams: { accountId: accountId }
        },
    ];
};


export const getEventHubNavigationLinks = (accountId: string, eventHubId: string, intl: IntlShape): INavigationLink[] => {
    const labels = getNavigationLabels(intl);
    const hasViewHubAttendeesPermission: boolean = hasPermission(Permission.VIEW_HUB_ATTENDEES);
    const hasHubAccess: boolean = hasPermission(Permission.EVENT_HUBS_TAB);

    return [
        {
            label: labels.EVENT_HUB_NAVIGATION.ATTENDEES,
            routeEnum: Routes.EventHubAttendees,
            routeParams: { accountId: accountId, eventHubId: eventHubId },
            isHidden: !hasViewHubAttendeesPermission
        },
        {
            label: labels.EVENT_HUB_NAVIGATION.HUB_CONFIGURATION,
            routeEnum: Routes.EventHubConfiguration,
            routeParams: { accountId: accountId, eventHubId: eventHubId },
            isHidden: !hasHubAccess
        }
    ];
};

export const getEventNavigationLinks = (options: { routeParams?: object }, intl: IntlShape): INavigationLink[] => {
    const labels = getNavigationLabels(intl);
    const hasPassportPermission: boolean = hasPermission(Permission.VIEW_PASSPORT_LINK) || hasPermission(Permission.PASSPORT_TAB);

    return [
        {
            label: labels.EVENT_NAVIGATION.SCHEDULE,
            routeEnum: Routes.Meetings,
            isHidden: !hasPermission(Permission.SCHEDULE_TAB),
            ...options
        },
        {
            label: labels.EVENT_NAVIGATION.ORGANIZATIONS,
            routeEnum: Routes.OrganizationsList,
            ...options
        },
        {
            label: labels.EVENT_NAVIGATION.ATTENDEES,
            routeEnum: Routes.AttendeesList,
            ...options
        },
        {
            label: labels.EVENT_NAVIGATION.ROOMS,
            routeEnum: Routes.RoomsList,
            ...options
        },
        {
            label: labels.EVENT_NAVIGATION.CONNECTIONS,
            routeEnum: Routes.ConnectionsList,
            isHidden: !hasPermission(Permission.CONNECTIONS_MANAGEMENT),
            ...options
        },
        {
            label: labels.EVENT_NAVIGATION.ASSETS,
            routeEnum: Routes.Assets,
            isHidden: !hasPermission(Permission.ASSETS_TAB),
            ...options
        },
        {
            label: labels.EVENT_NAVIGATION.ARTIFACTS,
            routeEnum: Routes.Artifacts,
            isHidden: !hasPermission(Permission.ARTIFACTS_TAB),
            ...options
        },
        {
            label: labels.EVENT_NAVIGATION.PASSPORT,
            routeEnum: Routes.PassportEdit,
            isHidden: !hasPassportPermission,
            ...options
        },
        {
            label: labels.EVENT_NAVIGATION.REPORTS,
            routeEnum: Routes.Reports,
            isHidden: !hasPermission(Permission.REPORTS_TAB),
            ...options
        },
        {
            label: labels.EVENT_NAVIGATION.CONTROL,
            routeEnum: Routes.Control,
            ...options,
        },
    ];
};

export const getZoomNavigationLinks = (intl: IntlShape) => {
    const labels = getNavigationLabels(intl);

    return [
        {
            label: labels.ZOOM_NAVIGATION.MEETINGS,
            routeEnum: Routes.ZoomScheduledMeetings,
        },
        {
            label: labels.ZOOM_NAVIGATION.USERS,
            routeEnum: Routes.TrackedZoomUsers
        },
        {
            label: labels.ZOOM_NAVIGATION.TELEPHONES,
            routeEnum: Routes.TelephoneNumberSets,
            isHidden: !hasPermission(Permission.TELEPHONE_NUMBER_SETS)
        }
    ];
};

export const getEventTrackerLinks = (intl: IntlShape) => {
    const labels = getNavigationLabels(intl);

    return [
        {
            label: labels.EVENT_TRACKER_NAVIGATION.UPCOMING,
            routeEnum: Routes.EventTrackerUpcoming,
        },
        {
            label: labels.EVENT_TRACKER_NAVIGATION.PAST,
            routeEnum: Routes.EventTrackerPast,
        },
        {
            label: labels.EVENT_TRACKER_NAVIGATION.DELIVERED,
            routeEnum: Routes.EventTrackerDelivered,
            isHidden: !hasPermission(Permission.ACCESS_COMPLETED_EVENTS)
        }
    ];
};

export const getManageAttributesNavigationLinks = (accountId: string, intl: IntlShape): INavigationLink[] => {
    const labels = getNavigationLabels(intl);

    return [
        {
            label: labels.ATTRIBUTES_NAVIGATION.SETTINGS,
            routeEnum: Routes.AccountAdminSettings,
            routeParams: { accountId: accountId }
        },
        {
            label: labels.ATTRIBUTES_NAVIGATION.EVENT_ATTRIBUTES,
            routeEnum: Routes.AccountAdminEventAttributes,
            routeParams: { accountId: accountId },
            isHidden: !hasPermission(Permission.VIEW_EVENT_ATTRIBUTES)
        }
    ];
};

export const getOeDataNavigationLinks = (intl: IntlShape): INavigationLink[] => {
    const labels = getNavigationLabels(intl);

    return [
        {
            label: labels.OE_DATA_NAVIGATION.ORGANIZATIONS,
            routeEnum: Routes.OeDataOrganizations
        }
    ];
};

export const CSV_TEMPLATE_LINK = 'https://www.openexc.com/csv-templates';
